import React, { useEffect, useMemo, useRef, useState } from 'react';

import type { Country } from '@skippay/core/@types/country';
import { DEFAULT_COUNTRY, getCurrency } from '@skippay/core/hooks/useUserCountryRelatedInfo';

import { useJarabotWidgetReady } from '../useJarabotWidgetReady';
import { IS_NON_PROD, getJarabotDelay, isSupportedRuntime } from '../utils';
import {
	AnyMutationSource,
	ContextAdapter,
	ContextEnv,
	DomainSource,
	SourceData,
	isDerivedSource,
	isMutationSource,
} from './adapters/types';
import { useJarabotContextOwnership } from './ownership';
import { pushCustomAttributes, pushIdentity } from './push';
import { MutationSourceRunner, QuerySourceRunner } from './sourceRunners';
import { JarabotIdentity, JarabotSurface } from './types';
import { useCustomerContextModel } from './useCustomerContextModel';

const STAGGER_KEY = '__jarabot_customer_context_stagger';
const DEFAULT_STAGGER_MS = 250;

export interface JarabotCustomerContextProps {
	surface: JarabotSurface;
	adapter: ContextAdapter;
	country?: Country;
	identity?: JarabotIdentity;
}

interface SourceEntry<TSource> {
	key: string;
	source: TSource;
	delayMs: number;
}

interface RunnableEntries {
	queryEntries: Array<SourceEntry<DomainSource<SourceData>>>;
	mutationEntries: Array<SourceEntry<AnyMutationSource>>;
}

const isEnabled = (surface: JarabotSurface, country?: Country): boolean =>
	isSupportedRuntime() &&
	(surface === JarabotSurface.SELFCARE || (IS_NON_PROD && country === 'CZ'));

function ActiveCustomerContext({
	surface,
	adapter,
	country,
	identity,
}: JarabotCustomerContextProps): JSX.Element {
	const resolvedCountry = country ?? DEFAULT_COUNTRY;
	const [staggerMs] = useState(() => getJarabotDelay(STAGGER_KEY, DEFAULT_STAGGER_MS));
	const derivedSources = useMemo(() => adapter.filter(isDerivedSource), [adapter]);
	const { revision, getModel, mergeModel } = useCustomerContextModel(derivedSources);
	const env = useMemo<ContextEnv>(() => ({ country: resolvedCountry }), [resolvedCountry]);
	const hasPushedIdentityRef = useRef(false);

	const { queryEntries, mutationEntries } = useMemo<RunnableEntries>(() => {
		const runnable: RunnableEntries = { queryEntries: [], mutationEntries: [] };

		adapter.forEach((source, index) => {
			if (isDerivedSource(source)) return;
			const entry = { key: String(index), delayMs: index * staggerMs };
			if (isMutationSource(source)) runnable.mutationEntries.push({ ...entry, source });
			else runnable.queryEntries.push({ ...entry, source });
		});

		return runnable;
	}, [adapter, staggerMs]);

	useEffect(() => {
		if (hasPushedIdentityRef.current) return;
		hasPushedIdentityRef.current = true;

		if (identity) pushIdentity(identity, resolvedCountry);
		pushCustomAttributes({ currency: getCurrency(resolvedCountry), surface });
	}, [identity, resolvedCountry, surface]);

	return (
		<>
			{queryEntries.map(({ key, source, delayMs }) => (
				<QuerySourceRunner
					key={key}
					source={source}
					delayMs={delayMs}
					getModel={getModel}
					mergeModel={mergeModel}
				/>
			))}
			{mutationEntries.map(({ key, source, delayMs }) => (
				<MutationSourceRunner
					key={key}
					source={source}
					delayMs={delayMs}
					env={env}
					revision={revision}
					getModel={getModel}
					mergeModel={mergeModel}
				/>
			))}
		</>
	);
}

export function JarabotCustomerContext(props: JarabotCustomerContextProps): JSX.Element | null {
	const { surface, country } = props;
	const isWidgetReady = useJarabotWidgetReady();
	const isOwner = useJarabotContextOwnership(surface, isEnabled(surface, country) && isWidgetReady);

	if (!isOwner) return null;

	return <ActiveCustomerContext {...props} />;
}
